// Japan fund page UI Task 6 (spec 2026-09-28-japan-fund-page-ui): per-fund
// page registry. Dashboard.vue renders each fund page's tab bar, Signal tab
// and Backtest tab from here instead of per-portfolio v-if chains.
//
// Kept separate from fundConfig.ts on purpose: this module imports .vue
// components, and fundConfig.ts must stay component-free so it can be
// imported anywhere (including by these components) without a cycle.
import type { Component } from 'vue'
import { api } from '../../api/client'
import type { SignalData, EnrichedSynthNavData } from '../../api/client'
import { FUND_REGISTRY, FUND_CONFIG, HOME_PAGE, type Fund, type FundConfig, type PortfolioSlug } from './fundConfig'
import BacktestPanel from '../BacktestPanel.vue'
import FundBacktestPanel from '../backtest/FundBacktestPanel.vue'
import { GOLD_BACKTEST_PANEL, GGE_BACKTEST_PANEL, type FundBacktestConfig } from '../backtest/fundBacktestPanels'
import BgaJhSignalTab from '../fundtabs/BgaJhSignalTab.vue'
import FundSignalTab from '../fundtabs/FundSignalTab.vue'
import { GOLD_SIGNAL_PANEL, GGE_SIGNAL_PANEL } from '../signal/fundSignalPanels'
import type { FundSignalPanelConfig } from '../signal/fundSignalPanel'
import JapanSignalTab from '../fundtabs/JapanSignalTab.vue'
import JapanBacktestPanel from '../fundtabs/JapanBacktestPanel.vue'
import CharterisSignalTab from '../fundtabs/CharterisSignalTab.vue'
import AmatiSignalTab from '../fundtabs/AmatiSignalTab.vue'
import { signRuleTab } from '../fundtabs/signRuleTab'
import BgaSparklineStrip from '../strips/BgaSparklineStrip.vue'
import JhSparklineStrip from '../strips/JhSparklineStrip.vue'
import GoldStrip from '../strips/GoldStrip.vue'
import GgeStrip from '../strips/GgeStrip.vue'
import JapanSparklineStrip from '../strips/JapanSparklineStrip.vue'
import CharterisSparklineStrip from '../strips/CharterisSparklineStrip.vue'

export type FundTabId = 'signal'|'pnl'|'nav'|'risk'|'kt'|'account'|'exec'|'devops'|'backtest'|'admin'|'strategy'
export const FUND_TAB_ORDER: ReadonlyArray<FundTabId> =
  ['signal','pnl','nav','risk','kt','account','exec','devops','backtest','admin','strategy']
export const SUPERUSER_TABS: ReadonlySet<FundTabId> = new Set<FundTabId>(['backtest','admin','strategy'])
export type TabState = { kind: 'enabled' } | { kind: 'disabled'; reason: string }

/** Dashboard's shared state, passed to every signalTab component as one `ctx` prop. */
export interface SignalTabCtx {
  intSignal: SignalData | null; intEnriched: EnrichedSynthNavData | null
  activeFundReturnPct: number | null; jhReturnPct: number | null; jhSparkValues: number[] | undefined
  nasReturnPct: number | null; gbpusdReturn: number | null
  // Plan 2026-10-04 Task 3: minimal Japan-strip inputs. Null on non-Japan
  // pages. Task 4a folded the dedicated pollJapanStrip poller into the
  // japan.feeds entry (japanSignal, 60s) — Dashboard derives these two
  // fields from feedData via the japanSignalFeed computed.
  japanAnchorPrice: number | null
  japanThresholdBps: number | null
}

/** One integrity feed the Dashboard polls for this fund, whatever tab is open. */
export interface FundFeedSpec {
  /** Stable key — becomes the lookup id in feedData. */
  id: string
  intervalMs: number
  /** Wraps the api call. Thrown errors are caught by the shell and stored as null. */
  fetch: () => Promise<unknown>
  /**
   * Final-review fix wave (2026-10-04): when true, a failed tick keeps the
   * previous feedData value instead of nulling it. Default false matches
   * every pre-refactor poller that DID null on failure (goldSignal,
   * ggeSignal, goldEnriched, goldKtLatest, ggeKtLatest, ktAttributionBga/Jh,
   * ktIntradayLive/Jh). Set true only on feeds whose pre-refactor poller
   * deliberately kept the previous value — see the per-feed comments below.
   */
  keepPreviousOnFail?: boolean
  /**
   * 2026-11-02: set when the route is one of the per-user fund routes the API
   * gates with RequireFundAccess (/signal/<fund>, /synth-nav/enriched?fund=).
   * The Dashboard shell skips the tick (stores null, no request) while the
   * viewer lacks access to that fund — paolo restricting his own BGA/GOLD had
   * every fund page 403ing these every 5 s. Leave unset on routes the server
   * does not gate per user (K_t, kt-latest, integrity/*).
   */
  fund?: Fund
}

export interface FundPageBase {
  slug: PortfolioSlug
  fund: Fund
  label: string
  hostnames: string[]
  requiresEntitlement: boolean
  display: FundConfig
}

export type FundPage =
  | (FundPageBase & {
      kind: 'fund'
      tabs: Record<FundTabId, TabState>
      topStrip: Component | null
      signalTab: Component
      backtestTab: Component | null
      feeds: ReadonlyArray<FundFeedSpec>
      /**
       * Follow-up (2026-10-04 housekeeping): replaces the
       * `usePortfolio().portfolio === 'bga'` check that used to live inside
       * BgaJhSignalTab.vue. True for bga only — jh (which shares the same
       * signalTab component) and every other fund are false.
       */
      showNewsTicker: boolean
      /**
       * Option C (2026-10-11): the shared momentum-fund signal panel's
       * per-fund config. Set on pages whose signalTab is FundSignalTab
       * (gold, gge); Dashboard passes it as that tab's `config` prop.
       */
      signalPanel?: FundSignalPanelConfig
      /**
       * Option C PR 2 (2026-10-11): the shared step-aside backtest panel's
       * per-fund config. Set on pages whose backtestTab is FundBacktestPanel
       * (gold, gge); Dashboard passes it as that panel's `config` prop.
       */
      backtestPanel?: FundBacktestConfig
    })
  | (FundPageBase & { kind: 'overlay' })

/** FOF spec 2026-10-12: the FOF home page — a page, not a fund (no fund, feeds, strip or tabs map).
 *  Deliberately NOT a FUND_PAGES entry: FUND_PAGES is keyed by fund slug. Dashboard picks FOF_PAGE
 *  when usePage() is 'fof'. */
export type HomePage = { kind: 'home'; slug: 'fof'; label: string; hostnames: string[] }
export const FOF_PAGE: HomePage = { kind: 'home', slug: 'fof', label: HOME_PAGE.label, hostnames: [...HOME_PAGE.hostnames] }

const ALL_ENABLED: Record<FundTabId, TabState> = Object.fromEntries(
  FUND_TAB_ORDER.map(t => [t, { kind: 'enabled' }])) as Record<FundTabId, TabState>

function base(slug: PortfolioSlug): FundPageBase {
  const reg = FUND_REGISTRY[slug]
  // Follow-up (2026-10-04 housekeeping): copy on build — a FundPage must
  // never alias the shared FUND_REGISTRY/FUND_CONFIG objects, so mutating
  // one page's hostnames/display can't leak into the registry (or another
  // page built from the same registry entry).
  return {
    slug: reg.slug, fund: reg.fund, label: reg.label, hostnames: [...reg.hostnames],
    requiresEntitlement: reg.requiresEntitlement, display: { ...FUND_CONFIG[reg.fund] },
  }
}

export const FUND_PAGES: Record<PortfolioSlug, FundPage> = {
  // Plan 2026-10-04 Task 4b: cluster-B (per-fund K_t) integrity feeds.
  // Intervals copied verbatim from the pre-refactor Dashboard.vue pollers —
  // see the removed pollIntegrityKt (JH/GGE arms)/KtAttribution/
  // KtIntradayLive(Jh) for provenance.
  bga:  { ...base('bga'),  kind: 'fund', tabs: ALL_ENABLED, showNewsTicker: true,
          topStrip: BgaSparklineStrip, signalTab: BgaJhSignalTab, backtestTab: BacktestPanel, feeds: [
            { id: 'ktAttributionBga', intervalMs: 5 * 60_000, fetch: () => api.ktAttribution('bga') },
            { id: 'ktIntradayLive',   intervalMs: 60_000,     fetch: () => api.ktIntradayLiveHealth() },
          ] },
  jh:   { ...base('jh'),   kind: 'fund', tabs: ALL_ENABLED, showNewsTicker: false,
          topStrip: JhSparklineStrip, signalTab: BgaJhSignalTab, backtestTab: BacktestPanel, feeds: [
            // 2026-08-30 rationale (pollIntegrityKt's JH arm, pre-refactor):
            // keep the previous series on a failed poll — nulling painted
            // three FAIL rows for a whole 5-min cycle on a transient blip.
            { id: 'ktJh',             intervalMs: 5 * 60_000, fetch: () => api.getSyntheticKt('jh'), keepPreviousOnFail: true },
            { id: 'ktAttributionJh',  intervalMs: 5 * 60_000, fetch: () => api.ktAttribution('jh') },
            { id: 'ktIntradayLiveJh', intervalMs: 60_000,     fetch: () => api.ktIntradayLiveHealth('JH') },
          ] },
  // Plan 2026-10-04 Task 4a: cluster-A integrity feeds (intervals copied
  // verbatim from the pre-refactor Dashboard.vue pollers — see the removed
  // pollIntegrityGoldSignal/GoldEnriched/KtLatest for provenance).
  gold: { ...base('gold'), kind: 'fund', tabs: ALL_ENABLED, showNewsTicker: false,
          topStrip: GoldStrip,  signalTab: FundSignalTab, signalPanel: GOLD_SIGNAL_PANEL,
          backtestTab: FundBacktestPanel, backtestPanel: GOLD_BACKTEST_PANEL, feeds: [
            { id: 'goldSignal',   intervalMs: 5 * 60_000, fetch: () => api.goldSignal(), fund: 'GOLD' },
            { id: 'goldEnriched', intervalMs: 5_000,      fetch: () => api.enrichedSynthNav('GOLD'), fund: 'GOLD' },
            { id: 'goldKtLatest', intervalMs: 5 * 60_000, fetch: () => api.goldKtLatest() },
          ] },
  gge:  { ...base('gge'),  kind: 'fund', tabs: ALL_ENABLED, showNewsTicker: false,
          topStrip: GgeStrip,   signalTab: FundSignalTab, signalPanel: GGE_SIGNAL_PANEL,
          backtestTab: FundBacktestPanel, backtestPanel: GGE_BACKTEST_PANEL, feeds: [
            // cluster A
            { id: 'ggeSignal',   intervalMs: 5 * 60_000, fetch: () => api.ggeSignal(), fund: 'GGE' },
            { id: 'ggeKtLatest', intervalMs: 5 * 60_000, fetch: () => api.ggeKtLatest() },
            // cluster B (Task 4b)
            // 2026-08-30 rationale (pollIntegrityKt's GGE arm, pre-refactor):
            // keep the previous series on a failed poll — nulling painted
            // three FAIL rows for a whole 5-min cycle on a transient blip.
            { id: 'ktGge',       intervalMs: 5 * 60_000, fetch: () => api.getSyntheticKt('gge'), keepPreviousOnFail: true },
          ] },
  otp:  { ...base('otp'),  kind: 'overlay' },
  // Task 12 made Japan signal-only; plan 2026-09-29 Task 15 opened P&L, Risk and Exec; Task 16 adds the Backtest tab (the decided shared-pot backtest). K_t stays greyed with its reason.
  // Task 4a folds the Task-3 pollJapanStrip block into this one feed.
  japan: { ...base('japan'), kind: 'fund', showNewsTicker: false,
           topStrip: JapanSparklineStrip, signalTab: JapanSignalTab, backtestTab: JapanBacktestPanel,
           feeds: [
             // The deleted pollJapanStrip kept the previous anchor/threshold
             // on a failed poll (only overwrote when `available !== false`)
             // — same 2026-08-30 rationale as ktJh/ktGge above.
             { id: 'japanSignal', intervalMs: 60_000, fetch: () => api.japanSignal(), keepPreviousOnFail: true },
           ], tabs: { ...ALL_ENABLED,
             kt: { kind: 'disabled', reason: 'K_t tracking not yet available for Japan' } } },
  // Charteris Phase 2.5 Task 5 (2026-10-07): the second gated fund. Top strip
  // added 2026-10-07 (Paolo): CHARTERIS · SI · GC · FX mini charts (self-polling). Plain BacktestPanel
  // (no JapanBacktestPanel-style pot view); backtestSignRuleOnly drives the sign-rule-only mode.
  // Charteris Phase 3 (spec 2026-10-05 §7) opened P&L, Risk and Exec as Japan's plan 2026-09-29
  // Task 15 did — each is per-user and entitlement-gated, so the page stays inert until the grant.
  silver: { ...base('silver'), kind: 'fund', showNewsTicker: false,
               topStrip: CharterisSparklineStrip, signalTab: CharterisSignalTab, backtestTab: BacktestPanel,
               // No Dashboard feed: the strip and CharterisSignalTab poll their own APIs.
               feeds: [], tabs: ALL_ENABLED },
  // AMATI Phase 2.5 lean fund page (2026-10-11): the third gated fund, under its
  // commodity name Copper. No top strip, no admin card yet (K_t tab enabled 2026-10-12: shared panel + Signal-tab holdings card) (deferred —
  // docs/runbooks/amati-fund-dark.md). Plain BacktestPanel in sign-rule-only mode
  // (registry backtestSignRuleOnly). AMATI Phase 3 (Paolo, 2026-10-13) opened P&L, Risk and Exec as
  // Charteris's Phase 3 did — each is per-user and entitlement-gated.
  copper: { ...base('copper'), kind: 'fund', showNewsTicker: false,
            topStrip: null, signalTab: AmatiSignalTab, backtestTab: BacktestPanel,
            // No Dashboard feed: AmatiSignalTab polls its own API.
            feeds: [], tabs: ALL_ENABLED },
  // MINERS Phase 1C (2026-10-25): the fourth gated fund and the first on the generic fund pack. Generic
  // sign-rule Signal tab (registry signalRoot/signalAnchorLabel) and the plain sign-rule-only BacktestPanel.
  // Phase 3 (2026-10-26, plan 2026-10-26-miners-phase3-shared-pot) opened P&L, Risk and Exec as Charteris's
  // and Amati's Phase 3 did — each is per-user and entitlement-gated; no K_t yet.
  miners: { ...base('miners'), kind: 'fund', showNewsTicker: false,
            topStrip: null, signalTab: signRuleTab('miners'), backtestTab: BacktestPanel,
            // No Dashboard feed: SignRuleSignalTab polls its own API.
            feeds: [], tabs: { ...ALL_ENABLED,
              kt: { kind: 'disabled', reason: 'K_t tracking not yet available for Miners' } } },
  // JAPANESG (2026-10-28): the ninth fund, second on the generic fund pack, added alongside JAPAN. Generic sign-rule
  // Signal tab + plain sign-rule-only BacktestPanel; only Signal, Backtest and the shared tabs are live.
  // P&L, Risk, Exec open when Paolo joins the pot; no K_t yet.
  japanesg: { ...base('japanesg'), kind: 'fund', showNewsTicker: false,
              topStrip: null, signalTab: signRuleTab('japanesg'), backtestTab: BacktestPanel,
              // No Dashboard feed: SignRuleSignalTab polls its own API.
              feeds: [], tabs: { ...ALL_ENABLED,
                pnl: { kind: 'disabled', reason: 'P&L opens at Phase 3' },
                risk: { kind: 'disabled', reason: 'Risk opens at Phase 3' },
                exec: { kind: 'disabled', reason: 'Exec opens at Phase 3' },
                kt: { kind: 'disabled', reason: 'K_t tracking not yet available for Japan ESG' } } },
  // TECH (2026-10-28): the tenth fund, third on the generic fund pack, added alongside JH. Generic sign-rule
  // Signal tab + plain sign-rule-only BacktestPanel; only Signal, Backtest and the shared tabs are live.
  // P&L, Risk, Exec open when Paolo joins the pot; no K_t yet.
  tech: { ...base('tech'), kind: 'fund', showNewsTicker: false,
          topStrip: null, signalTab: signRuleTab('tech'), backtestTab: BacktestPanel,
          // No Dashboard feed: SignRuleSignalTab polls its own API.
          feeds: [], tabs: { ...ALL_ENABLED,
            pnl: { kind: 'disabled', reason: 'P&L opens at Phase 3' },
            risk: { kind: 'disabled', reason: 'Risk opens at Phase 3' },
            exec: { kind: 'disabled', reason: 'Exec opens at Phase 3' },
            kt: { kind: 'disabled', reason: 'K_t tracking not yet available for Tech' } } },
  // ELECTRUM (2026-10-31): the eleventh fund, fourth on the generic fund pack, added alongside Amati. Generic sign-rule
  // Signal tab + plain sign-rule-only BacktestPanel; only Signal, Backtest and the shared tabs are live.
  // P&L, Risk, Exec open when Paolo joins the pot; no K_t yet.
  electrum: { ...base('electrum'), kind: 'fund', showNewsTicker: false,
          topStrip: null, signalTab: signRuleTab('electrum'), backtestTab: BacktestPanel,
          // No Dashboard feed: SignRuleSignalTab polls its own API.
          feeds: [], tabs: { ...ALL_ENABLED,
            pnl: { kind: 'disabled', reason: 'P&L opens at Phase 3' },
            risk: { kind: 'disabled', reason: 'Risk opens at Phase 3' },
            exec: { kind: 'disabled', reason: 'Exec opens at Phase 3' },
            kt: { kind: 'disabled', reason: 'K_t tracking not yet available for Electrum' } } },
  // JHTECH, ASIATECH, BARINGSOIL, SPWORLD (2026-10-31): funds twelve to fifteen on the generic fund pack, dark additions
  // shaped exactly like Electrum's page (generic sign-rule Signal tab, plain BacktestPanel, no Dashboard feed);
  // P&L, Risk, Exec open only if Paolo ever joins one to the pot; no K_t.
  jhtech: { ...base('jhtech'), kind: 'fund', showNewsTicker: false,
          topStrip: null, signalTab: signRuleTab('jhtech'), backtestTab: BacktestPanel,
          feeds: [], tabs: { ...ALL_ENABLED,
            pnl: { kind: 'disabled', reason: 'P&L opens at Phase 3' },
            risk: { kind: 'disabled', reason: 'Risk opens at Phase 3' },
            exec: { kind: 'disabled', reason: 'Exec opens at Phase 3' },
            kt: { kind: 'disabled', reason: 'K_t tracking not yet available for JHTech' } } },
  asiatech: { ...base('asiatech'), kind: 'fund', showNewsTicker: false,
          topStrip: null, signalTab: signRuleTab('asiatech'), backtestTab: BacktestPanel,
          feeds: [], tabs: { ...ALL_ENABLED,
            pnl: { kind: 'disabled', reason: 'P&L opens at Phase 3' },
            risk: { kind: 'disabled', reason: 'Risk opens at Phase 3' },
            exec: { kind: 'disabled', reason: 'Exec opens at Phase 3' },
            kt: { kind: 'disabled', reason: 'K_t tracking not yet available for AsiaTech' } } },
  baringsoil: { ...base('baringsoil'), kind: 'fund', showNewsTicker: false,
          topStrip: null, signalTab: signRuleTab('baringsoil'), backtestTab: BacktestPanel,
          feeds: [], tabs: { ...ALL_ENABLED,
            pnl: { kind: 'disabled', reason: 'P&L opens at Phase 3' },
            risk: { kind: 'disabled', reason: 'Risk opens at Phase 3' },
            exec: { kind: 'disabled', reason: 'Exec opens at Phase 3' },
            kt: { kind: 'disabled', reason: 'K_t tracking not yet available for BaringsOil' } } },
  spworld: { ...base('spworld'), kind: 'fund', showNewsTicker: false,
          topStrip: null, signalTab: signRuleTab('spworld'), backtestTab: BacktestPanel,
          feeds: [], tabs: { ...ALL_ENABLED,
            pnl: { kind: 'disabled', reason: 'P&L opens at Phase 3' },
            risk: { kind: 'disabled', reason: 'Risk opens at Phase 3' },
            exec: { kind: 'disabled', reason: 'Exec opens at Phase 3' },
            kt: { kind: 'disabled', reason: 'K_t tracking not yet available for SPWorld' } } },
  // SMALLCAP (2026-10-31): fund sixteen, a dark addition shaped like SPWorld's page.
  smallcap: { ...base('smallcap'), kind: 'fund', showNewsTicker: false,
          topStrip: null, signalTab: signRuleTab('smallcap'), backtestTab: BacktestPanel,
          feeds: [], tabs: { ...ALL_ENABLED,
            pnl: { kind: 'disabled', reason: 'P&L opens at Phase 3' },
            risk: { kind: 'disabled', reason: 'Risk opens at Phase 3' },
            exec: { kind: 'disabled', reason: 'Exec opens at Phase 3' },
            kt: { kind: 'disabled', reason: 'K_t tracking not yet available for SmallCap' } } },
}

/** Typed read of a feed value stored in the Dashboard's feedData bag. */
export function feed<T>(id: string, data: Record<string, unknown>): T | null {
  const v = data[id]
  return (v == null ? null : v) as T | null
}

/**
 * The tab bar for a fund page: FUND_TAB_ORDER, minus SUPERUSER_TABS for
 * standard users. Disabled tabs are INCLUDED (rendered greyed, Task 7).
 */
export function tabsFor(page: FundPage & { kind: 'fund' }, isSuperUser: boolean): FundTabId[] {
  return FUND_TAB_ORDER.filter(t => t in page.tabs && (isSuperUser || !SUPERUSER_TABS.has(t)))
}
